import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { DebugContextType, DebugState, DebugSettings, AIRequest, AIResponse } from '../types/debug';
import { useSimpleToast } from '../hooks/use-simple-toast';
import { DebugInterceptor } from '../services/debug-interceptor';
import { 
  createDebugError, 
  logDebugError, 
  shouldDisableDebugMode, 
  autoDisableDebugMode,
  getDebugErrorMessage 
} from '../utils/debug-error-utils';

const DEBUG_SETTINGS_KEY = 'ai-character-studio-debug-settings';

const defaultDebugState: DebugState = {
  isEnabled: false,
  requestHistory: [],
};

const defaultDebugSettings: DebugSettings = {
  isEnabled: false,
  showTimestamps: true,
  autoShowResponses: true,
};

const DebugContext = createContext<DebugContextType | undefined>(undefined);

interface DebugProviderProps {
  children: ReactNode;
}

export function DebugProvider({ children }: DebugProviderProps) {
  const [debugState, setDebugState] = useState<DebugState>(defaultDebugState);
  const [debugSettings, setDebugSettings] = useState<DebugSettings>(defaultDebugSettings);
  const [debugError, setDebugError] = useState<string | null>(null);
  const { toast } = useSimpleToast();

  // Load debug settings from localStorage on mount
  useEffect(() => {
    try {
      const savedSettings = localStorage.getItem(DEBUG_SETTINGS_KEY);
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings) as DebugSettings;
        setDebugSettings(parsed);
        setDebugState(prev => ({ ...prev, isEnabled: parsed.isEnabled }));
      }
    } catch (error) {
      console.warn('Failed to load debug settings from localStorage:', error);
    }
  }, []);

  // Save debug settings to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(DEBUG_SETTINGS_KEY, JSON.stringify(debugSettings));
    } catch (error) {
      console.warn('Failed to save debug settings to localStorage:', error);
    }
  }, [debugSettings]);

  const toggleDebug = () => {
    setDebugState((prev) => {
      const newEnabled = !prev.isEnabled;
      if (!newEnabled) {
        DebugInterceptor.clearQueues();
      }
      return { ...prev, isEnabled: newEnabled };
    });
    setDebugSettings((prev) => ({ ...prev, isEnabled: !prev.isEnabled }));
  };

  const interceptRequest = async (request: AIRequest): Promise<AIRequest | null> => {
    if (!debugState.isEnabled) {
      return request;
    }

    try {
      // Clear any previous debug errors
      setDebugError(null);

      // Use the DebugInterceptor service to show the prompt editor
      return await DebugInterceptor.showPromptEditor(request);
    } catch (error) {
      console.error('Debug request interception failed:', error);
      setDebugError(error instanceof Error ? error.message : 'Debug interception failed');
      
      // Show user feedback
      toast({
        title: 'Debug Error',
        description: 'Debug mode failed, continuing with normal request',
      });

      // Fallback to normal request
      return request;
    }
  };

  const showResponse = (response: AIResponse) => {
    if (!debugState.isEnabled) {
      return;
    }

    try {
      // Clear any previous debug errors
      setDebugError(null);

      // Add to request history
      setDebugState(prev => {
        const newHistory = [...prev.requestHistory];
        
        // Find matching request in history or add new entry
        const existingIndex = newHistory.findIndex(
          item => item.request.metadata.timestamp === response.metadata.timestamp
        );
        
        if (existingIndex >= 0) {
          newHistory[existingIndex] = { ...newHistory[existingIndex], response };
        } else {
          // If no matching request found, create a minimal entry
          newHistory.push({
            request: {
              provider: response.provider,
              model: response.model,
              prompts: { system: '', user: '' },
              metadata: {
                requestType: response.metadata.requestType as any,
                timestamp: response.metadata.timestamp,
              },
            },
            response,
          });
        }

        return {
          ...prev,
          requestHistory: newHistory.slice(-50), // Keep last 50 entries
        };
      });

      // Use the DebugInterceptor service to show the response viewer
      DebugInterceptor.showResponseViewer(response);
    } catch (error) {
      console.error('Debug response display failed:', error);
      setDebugError(error instanceof Error ? error.message : 'Debug response display failed');
      
      // Show user feedback
      toast({
        title: 'Debug Error',
        description: 'Failed to display debug response',
      });
    }
  };

  const clearHistory = () => {
    try {
      setDebugState(prev => ({
        ...prev,
        requestHistory: [],
        currentRequest: undefined,
        currentResponse: undefined,
      }));
      setDebugError(null);
    } catch (error) {
      console.error('Failed to clear debug history:', error);
      setDebugError(error instanceof Error ? error.message : 'Failed to clear debug history');
    }
  };

  const handleDebugError = (error: Error, context: string = 'unknown') => {
    const errorInfo = createDebugError(error.message, context, error, debugState);
    logDebugError(errorInfo);
    setDebugError(error.message);
    
    // Get user-friendly error message
    const { title, message, canRetry } = getDebugErrorMessage(error);
    
    // Show user feedback
    toast({
      title,
      description: message,
    });
    
    // Check if debug mode should be auto-disabled
    if (shouldDisableDebugMode()) {
      console.warn('Auto-disabling debug mode due to repeated errors');
      autoDisableDebugMode();
      
      toast({
        title: 'Debug Mode Disabled',
        description: 'Debug mode was automatically disabled due to repeated errors. You can re-enable it later.',
      });
    }
  };

  const clearDebugError = () => {
    setDebugError(null);
  };

  const clearCurrentRequest = () => {
    setDebugState(prev => ({ ...prev, currentRequest: undefined }));
  };

  const clearCurrentResponse = () => {
    setDebugState(prev => ({ ...prev, currentResponse: undefined }));
  };

  const contextValue: DebugContextType = {
    isDebugEnabled: debugState.isEnabled,
    toggleDebug,
    interceptRequest,
    showResponse,
    debugState,
    clearHistory,
    debugError,
    handleDebugError,
    clearDebugError,
    clearCurrentRequest,
    clearCurrentResponse,
  };

  return (
    <DebugContext.Provider value={contextValue}>
      {children}
    </DebugContext.Provider>
  );
}

export function useDebug(): DebugContextType {
  const context = useContext(DebugContext);
  if (context === undefined) {
    // Provide a safe no-op fallback to avoid runtime crashes if the provider is missing.
    // This can happen in isolated tests or if a component is rendered without App wrapping.
    console.warn('[DebugContext] DebugProvider not found. Falling back to no-op debug context.');
    return {
      isDebugEnabled: false,
      toggleDebug: () => {},
      interceptRequest: async (request) => request,
      showResponse: () => {},
      debugState: {
        isEnabled: false,
        requestHistory: [],
      },
      clearHistory: () => {},
      debugError: null,
      handleDebugError: (_error: Error) => {},
      clearDebugError: () => {},
      clearCurrentRequest: () => {},
      clearCurrentResponse: () => {},
    };
  }
  return context;
}